import type {
  CreateSupplierRequest,
  GstCaptchaDto,
  GstLookupDetails,
  GstRegistrationType,
  GstStateCode,
  SupplierDto,
} from '@gsls/shared';
import { GST_STATE_CODES, GST_STATES, formatGstRegistrationDate, gstRegistrationDateToIso, gstStateCodeFromGstin, isValidGstin } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  inputClass,
} from '../components/ui';
import { CodeField } from '../components/CodeField';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { ApiError, api, describeApiError } from '../lib/api';
import { Can, useSession } from '../lib/session';

/**
 * Suppliers, including their GST details.
 *
 * The GSTIN is checked in the browser as it is typed, using the *same* function
 * the API uses (`isValidGstin` from shared), so the feedback cannot disagree with
 * what the server will accept. A GSTIN that passes also tells the form which
 * state it belongs to, and the state stops being a free choice at that point —
 * it is on the certificate, not a matter of opinion.
 */
interface SupplierForm {
  name: string;
  contact: string;
  gstin: string;
  gstRegistrationType: '' | GstRegistrationType;
  gstStateCode: '' | GstStateCode;
  /** The portal's record, carried through the form so it is saved with the supplier. */
  gstLegalName: string;
  gstTradeName: string;
  gstStatus: string;
  gstTaxpayerType: string;
  gstConstitution: string;
  /** ISO `YYYY-MM-DD`. */
  gstRegisteredOn: string;
  gstPrincipalPlace: string;
  gstNatureOfBusiness: string[];
  notes: string;
}

const EMPTY_FORM: SupplierForm = {
  name: '',
  contact: '',
  gstin: '',
  gstRegistrationType: '',
  gstStateCode: '',
  gstLegalName: '',
  gstTradeName: '',
  gstStatus: '',
  gstTaxpayerType: '',
  gstConstitution: '',
  gstRegisteredOn: '',
  gstPrincipalPlace: '',
  gstNatureOfBusiness: [],
  notes: '',
};

const EMPTY_CODE = '';

const REGISTRATION_TYPES: { value: '' | GstRegistrationType; label: string }[] = [
  { value: '', label: 'Not recorded yet' },
  { value: 'regular', label: 'Regular' },
  { value: 'composition', label: 'Composition' },
  { value: 'unregistered', label: 'Unregistered' },
];

function formFrom(supplier: SupplierDto): SupplierForm {
  return {
    name: supplier.name,
    contact: supplier.contact ?? '',
    gstin: supplier.gstin ?? '',
    gstRegistrationType: supplier.gstRegistrationType ?? '',
    gstStateCode: supplier.gstStateCode ?? '',
    gstLegalName: supplier.gstLegalName ?? '',
    gstTradeName: supplier.gstTradeName ?? '',
    gstStatus: supplier.gstStatus ?? '',
    gstTaxpayerType: supplier.gstTaxpayerType ?? '',
    gstConstitution: supplier.gstConstitution ?? '',
    gstRegisteredOn: supplier.gstRegisteredOn ?? '',
    gstPrincipalPlace: supplier.gstPrincipalPlace ?? '',
    gstNatureOfBusiness: supplier.gstNatureOfBusiness ?? [],
    notes: supplier.notes ?? '',
  };
}

/**
 * The GST and comment fields, shared by the create form and the inline editor so
 * the two cannot drift into asking for different things.
 */
function SupplierFields({
  value,
  onChange,
  disabled,
  gstinLocked = false,
}: {
  value: SupplierForm;
  onChange: (next: SupplierForm) => void;
  disabled?: boolean;
  /** True once a supplier already has a GSTIN: it is set once and then frozen. */
  gstinLocked?: boolean;
}) {
  const typedGstin = value.gstin.trim().toUpperCase();
  const validGstin = typedGstin.length > 0 && isValidGstin(typedGstin);
  const derivedState = validGstin ? gstStateCodeFromGstin(typedGstin) : null;
  const looksWrong = typedGstin.length > 0 && !validGstin;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Field
        label="GSTIN"
        hint={gstinLocked ? 'Locked' : 'Optional'}
        tooltip={
          gstinLocked
            ? 'A GSTIN is set once — it is the number invoices are filed against, so changing it would invalidate them. Deactivate this supplier and create a new one if it is wrong.'
            : "The 15-character number on the supplier's GST certificate. It is checked against its own check digit, so a mistyped character is caught here rather than on a tax invoice."
        }
      >
        <input
          className={`${inputClass} font-mono uppercase disabled:cursor-not-allowed disabled:opacity-60`}
          value={value.gstin}
          disabled={disabled || gstinLocked}
          onChange={(e) => onChange({ ...value, gstin: e.target.value })}
          placeholder="27AAPFU0939F1ZV"
          maxLength={15}
          aria-invalid={looksWrong}
          aria-readonly={gstinLocked}
        />
        {gstinLocked ? (
          <span className="mt-1 block text-xs text-ink-muted">
            Set once — deactivate this supplier and create a new one to change it.
          </span>
        ) : null}
        {looksWrong ? (
          <span className="mt-1 block text-xs text-red-700">
            Not a valid GSTIN — check the characters and the last digit.
          </span>
        ) : null}
        {derivedState ? (
          <span className="mt-1 block text-xs text-ink-muted">
            Registered in {GST_STATES[derivedState]} ({derivedState})
          </span>
        ) : null}
      </Field>

      <Field
        label="GST registration"
        hint="Optional"
        tooltip="Regular and composition dealers charge GST; an unregistered supplier issues no tax invoice, so no input credit is available. Leave it blank while the certificate is still being collected — that is not the same as unregistered."
      >
        <select
          className={inputClass}
          value={value.gstRegistrationType}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...value, gstRegistrationType: e.target.value as '' | GstRegistrationType })
          }
        >
          {REGISTRATION_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="GST state"
        hint={derivedState ? 'From the GSTIN' : 'Optional'}
        tooltip="The state the supplier is registered in — the first two digits of a GSTIN. When a GSTIN is present it comes from that, because the two are required to agree."
      >
        <select
          className={inputClass}
          value={derivedState ?? value.gstStateCode}
          // With a valid GSTIN the state is a fact, not a choice, so the field
          // is not offered as one.
          disabled={disabled || Boolean(derivedState)}
          onChange={(e) => onChange({ ...value, gstStateCode: e.target.value as '' | GstStateCode })}
        >
          <option value="">Not recorded yet</option>
          {GST_STATE_CODES.map((code) => (
            <option key={code} value={code}>
              {code} — {GST_STATES[code]}
            </option>
          ))}
        </select>
      </Field>

      <div className="sm:col-span-2 lg:col-span-3">
        <Field
          label="Comment"
          hint="Optional"
          tooltip="Free-text remarks: payment terms, who to chase, why they were deactivated. Visible to anyone who can see suppliers."
        >
          <textarea
            className={`${inputClass} min-h-20`}
            value={value.notes}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, notes: e.target.value })}
            placeholder="Net 30; invoices to accounts@…; certificate pending"
            maxLength={4000}
          />
        </Field>
      </div>

      <div className="sm:col-span-2 lg:col-span-3">
        <GstDetailsPanel form={value} />
      </div>

      <div className="sm:col-span-2 lg:col-span-3">
        <GstLookup
          gstin={value.gstin}
          disabled={disabled}
          onApply={(details) => onChange(applyLookup(value, details))}
        />
      </div>
    </div>
  );
}

/**
 * Build the payload both forms send.
 *
 * When a GSTIN is present its state is left out on purpose: the API derives it
 * (a GSTIN's first two digits *are* the state code), and sending it here would
 * be a second opinion that could contradict the certificate. The state is only
 * sent when there is no GSTIN to derive it from.
 */
function gstPayload(form: SupplierForm) {
  const typedGstin = form.gstin.trim().toUpperCase();
  const derivedState = typedGstin && isValidGstin(typedGstin) ? gstStateCodeFromGstin(typedGstin) : null;

  return {
    gstin: typedGstin || null,
    gstRegistrationType: form.gstRegistrationType || null,
    ...(derivedState ? {} : { gstStateCode: form.gstStateCode || null }),
    // The portal's record travels with the GSTIN: sent only while there is one, so
    // clearing the GSTIN clears the details (the API refuses them without a GSTIN)
    // rather than sending a record about a registration the supplier no longer has.
    ...(typedGstin
      ? {
          gstLegalName: form.gstLegalName || null,
          gstTradeName: form.gstTradeName || null,
          gstStatus: form.gstStatus || null,
          gstTaxpayerType: form.gstTaxpayerType || null,
          gstConstitution: form.gstConstitution || null,
          gstRegisteredOn: form.gstRegisteredOn || null,
          gstPrincipalPlace: form.gstPrincipalPlace || null,
          gstNatureOfBusiness: form.gstNatureOfBusiness,
        }
      : {}),
  };
}

/**
 * Folds what the portal returned into the form.
 *
 * The certificate is a fact, so its state, registration type and the rest of the
 * portal record overwrite whatever was there — they are answers to the same
 * question and the certificate wins (the API's `supplierGstIssues` and the
 * database CHECKs enforce the same). The name is a judgement about what we call
 * this supplier, so it is only filled while it is still blank.
 */
function applyLookup(form: SupplierForm, details: GstLookupDetails): SupplierForm {
  return {
    ...form,
    gstin: details.gstin,
    gstStateCode: details.gstStateCode ?? form.gstStateCode,
    gstRegistrationType: details.gstRegistrationType ?? form.gstRegistrationType,
    name: form.name.trim() ? form.name : (details.legalName ?? details.tradeName ?? form.name),
    // The portal's record, stored with the supplier so it does not need looking
    // up twice. The registration date is normalised to ISO on the way in.
    gstLegalName: details.legalName ?? '',
    gstTradeName: details.tradeName ?? '',
    gstStatus: details.status ?? '',
    gstTaxpayerType: details.taxpayerType ?? '',
    gstConstitution: details.constitutionOfBusiness ?? '',
    gstRegisteredOn: gstRegistrationDateToIso(details.registrationDate) ?? '',
    gstPrincipalPlace: details.principalPlaceOfBusiness ?? '',
    gstNatureOfBusiness: details.natureOfBusiness,
  };
}

/**
 * Reads a supplier's details from the GST portal.
 *
 * Two steps rather than one button, because the lookup is a captcha flow: fetch
 * an image, then send the GSTIN with the digits a person read off it. The captcha
 * is single-use — a wrong or reused one comes back as `invalid_gst_captcha` — so
 * a failed lookup fetches a fresh image instead of leaving a stale one on screen
 * for the next attempt to fail against.
 */
function GstLookup({
  gstin,
  disabled,
  onApply,
}: {
  gstin: string;
  disabled?: boolean;
  onApply: (details: GstLookupDetails) => void;
}) {
  const [captcha, setCaptcha] = useState<GstCaptchaDto | null>(null);
  const [answer, setAnswer] = useState('');

  const typed = gstin.trim().toUpperCase();
  const ready = isValidGstin(typed);

  const getCaptcha = useMutation({
    mutationFn: () => api.suppliers.gstCaptcha(),
    onSuccess: (data) => {
      setCaptcha(data);
      setAnswer('');
    },
  });

  const lookUp = useMutation({
    mutationFn: () =>
      api.suppliers.gstLookup({
        gstin: typed,
        captcha: answer,
        captchaCookie: captcha?.captchaCookie ?? '',
      }),
    onSuccess: (details) => {
      onApply(details);
      setCaptcha(null);
      setAnswer('');
    },
    onError: () => {
      // Single-use: whatever went wrong, this captcha is spent.
      setAnswer('');
      getCaptcha.mutate();
    },
  });

  const busy = getCaptcha.isPending || lookUp.isPending;

  return (
    <div className="rounded-lg border border-surface-border bg-surface/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-muted">
          Read the certificate and fill the fields from it, rather than retyping them.
        </p>
        <Button
          type="button"
          tone="ghost"
          disabled={disabled || busy || !ready}
          onClick={() => getCaptcha.mutate()}
        >
          {getCaptcha.isPending ? 'Fetching…' : captcha ? 'New captcha' : 'Fetch from GST portal'}
        </Button>
      </div>

      {!ready ? (
        <p className="mt-2 text-xs text-ink-muted">
          Enter a valid GSTIN above first — the portal is searched by number.
        </p>
      ) : null}

      {captcha ? (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <img
            src={captcha.captchaImage}
            alt="GST portal captcha"
            width={182}
            height={50}
            className="rounded border border-surface-border bg-white"
          />
          <Field label="Captcha">
            <input
              className={`${inputClass} w-32 font-mono`}
              value={answer}
              disabled={disabled || lookUp.isPending}
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              onChange={(e) => setAnswer(e.target.value.replace(/\D/g, ''))}
              placeholder="6 digits"
            />
          </Field>
          <Button
            type="button"
            disabled={disabled || lookUp.isPending || answer.length !== 6}
            onClick={() => lookUp.mutate()}
          >
            {lookUp.isPending ? 'Looking up…' : 'Look up'}
          </Button>
        </div>
      ) : null}

      <ErrorNote>
        {describeApiError(getCaptcha.error) || describeApiError(lookUp.error)}
      </ErrorNote>
    </div>
  );
}

/**
 * The portal's record, shown whether it was just fetched or loaded from a saved
 * supplier. Rendered from the form rather than from the lookup result, so the two
 * cannot disagree — a lookup writes into the same state this reads.
 */
function GstDetailsPanel({ form }: { form: SupplierForm }) {
  const rows: { label: string; value: string }[] = [
    { label: 'Legal name', value: form.gstLegalName || '—' },
    { label: 'Trade name', value: form.gstTradeName || '—' },
    { label: 'Status', value: form.gstStatus || '—' },
    { label: 'Taxpayer type', value: form.gstTaxpayerType || '—' },
    { label: 'Registered', value: formatGstRegistrationDate(form.gstRegisteredOn) ?? '—' },
    { label: 'Address', value: form.gstPrincipalPlace || '—' },
  ];

  const hasDetails = rows.some((row) => row.value !== '—') || form.gstNatureOfBusiness.length > 0;
  if (!hasDetails) return null;

  const notActive = form.gstStatus ? form.gstStatus.toLowerCase() !== 'active' : false;

  return (
    <div className="rounded-lg border border-surface-border bg-white/70 p-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
        As recorded on the portal
      </p>
      <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-2 text-xs">
            <dt className="w-24 shrink-0 text-ink-muted">{row.label}</dt>
            <dd className="text-ink-body">{row.value}</dd>
          </div>
        ))}
      </dl>
      {form.gstNatureOfBusiness.length > 0 ? (
        <p className="mt-1 text-xs text-ink-body">
          <span className="text-ink-muted">Nature of business: </span>
          {form.gstNatureOfBusiness.join(' · ')}
        </p>
      ) : null}
      {notActive ? (
        <p className="mt-2 text-xs text-amber-700">
          The portal shows this registration as “{form.gstStatus}”, not Active — check before buying.
        </p>
      ) : null}
      <p className="mt-2 text-xs text-ink-muted">Saved with the supplier when you save.</p>
    </div>
  );
}

export function SuppliersPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canManage = can('masterdata.manage');
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [code, setCode] = useState(EMPTY_CODE);
  const [form, setForm] = useState<SupplierForm>(EMPTY_FORM);
  const [editing, setEditing] = useState<{ id: string; form: SupplierForm } | null>(null);

  const listQuery = useQuery({
    queryKey: ['suppliers', { search, includeInactive }],
    queryFn: () => api.suppliers.list({ search, includeInactive, limit: 100 }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['suppliers'] });

  const createMutation = useMutation({
    mutationFn: (payload: CreateSupplierRequest) => api.suppliers.create(payload),
    onSuccess: () => {
      void invalidate();
      setForm(EMPTY_FORM);
      setCode(EMPTY_CODE);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (variables: { id: string; body: Parameters<typeof api.suppliers.update>[1] }) =>
      api.suppliers.update(variables.id, variables.body),
    onSuccess: () => {
      void invalidate();
      setEditing(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (supplier: SupplierDto) =>
      api.suppliers.update(supplier.id, { isActive: !supplier.isActive }),
    onSuccess: () => void invalidate(),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    createMutation.mutate({
      code: code.trim(),
      name: form.name.trim(),
      contact: form.contact.trim() || null,
      notes: form.notes.trim() || null,
      ...gstPayload(form),
    });
  }

  const suppliers = listQuery.data?.items ?? [];

  const supplierColumns: DataColumn<SupplierDto>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (supplier) => (
        <span className="font-mono text-xs">
          {supplier.code}
          {supplier.isActive ? null : (
            <span className="ml-2">
              <Badge tone="muted">Inactive</Badge>
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      primary: true,
      cell: (supplier) => (
        <>
          <span className="font-medium text-ink">{supplier.name}</span>
          {supplier.contact ? (
            <>
              <br />
              <span className="text-xs text-ink-muted">{supplier.contact}</span>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: 'gstin',
      header: 'GSTIN',
      cell: (supplier) => {
        const stateName = supplier.gstStateCode ? GST_STATES[supplier.gstStateCode] : null;
        if (supplier.gstin) {
          return (
            <>
              <span className="font-mono text-xs">{supplier.gstin}</span>
              <br />
              <span className="text-xs text-ink-muted">
                {stateName ?? '—'}
                {supplier.gstRegistrationType ? ` · ${supplier.gstRegistrationType}` : ''}
              </span>
            </>
          );
        }
        if (supplier.gstRegistrationType === 'unregistered') {
          return <Badge tone="muted">Unregistered</Badge>;
        }
        return <span className="text-xs text-ink-muted">Not recorded</span>;
      },
    },
    {
      key: 'comment',
      header: 'Comment',
      cell: (supplier) =>
        supplier.notes ? (
          <span className="block max-w-64 truncate text-xs text-ink-body" title={supplier.notes}>
            {supplier.notes}
          </span>
        ) : (
          <span className="text-xs text-ink-muted">—</span>
        ),
    },
  ];

  const supplierActions = canManage
    ? (supplier: SupplierDto) => (
        <>
          <Button
            tone="ghost"
            onClick={() => {
              updateMutation.reset();
              setEditing({ id: supplier.id, form: formFrom(supplier) });
            }}
          >
            Edit
          </Button>
          <Button
            tone={supplier.isActive ? 'danger' : 'ghost'}
            onClick={() => toggleMutation.mutate(supplier)}
          >
            {supplier.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </>
      )
    : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Suppliers"
        description="Who raw material was bought from, and the GST details their invoices are filed against."
      />

      <div className="mb-6">
        <InfoNote>
          A GSTIN is validated against its own check digit, so a mistyped character is caught here
          rather than on a tax invoice — and a saved record can never hold a state that disagrees
          with the GSTIN it came from.
        </InfoNote>
      </div>

      <Can capability="masterdata.manage">
        <Card className="mb-6">
          <CardBody>
            <h2 className="text-base font-bold text-ink">New supplier</h2>
            <form autoComplete="off" onSubmit={handleSubmit} className="mt-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <CodeField
                  label="Code"
                  value={code}
                  onChange={setCode}
                  queryKey={['supplier-suggest', form.name.trim()]}
                  queryFn={
                    form.name.trim().length >= 2
                      ? () => api.suppliers.suggestCode({ name: form.name.trim() })
                      : undefined
                  }
                  placeholder="SUP-ACME-001"
                  tooltip="Built from the supplier's name and a sequence number — a supplier whose initials are ACME suggests SUP-ACME-001. Accept the suggestion or type your own; it is fixed once created."
                  error={
                    createMutation.error instanceof ApiError && createMutation.error.status === 409
                      ? createMutation.error.message
                      : undefined
                  }
                />
                <Field label="Name">
                  <input
                    className={inputClass}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    placeholder="Supplier name"
                  />
                </Field>
                <Field label="Contact" hint="Optional">
                  <input
                    className={inputClass}
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="Phone or email"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <SupplierFields
                  value={form}
                  onChange={setForm}
                  disabled={createMutation.isPending}
                />
              </div>

              <ErrorNote>
                {describeApiError(createMutation.error)}
              </ErrorNote>
              <div className="mt-3">
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create supplier'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Can>

      <Card>
        <CardBody>
          <FilterBar activeCount={search ? 1 : 0}>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input
                name="supplierSearch"
                className={`${inputClass} max-w-xs`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code, name or GSTIN"
                aria-label="Search suppliers"
              />
              <label className="flex items-center gap-2 text-sm text-ink-body">
                <input
                  name="includeInactive"
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                Include inactive
              </label>
              {listQuery.isSuccess ? (
                <span className="text-sm text-ink-muted">{listQuery.data.total} supplier(s)</span>
              ) : null}
            </div>
          </FilterBar>

          {listQuery.isPending ? <Spinner /> : null}
          <ErrorNote>
            {describeApiError(toggleMutation.error)}
            {listQuery.isError ? 'Could not load suppliers.' : ''}
          </ErrorNote>

          {listQuery.isSuccess ? (
            <DataTable
              rows={suppliers}
              columns={supplierColumns}
              rowKey={(supplier) => supplier.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actionsHeader="Actions"
              actions={supplierActions}
              empty="No suppliers yet."
              detail={(supplier) =>
                editing?.id === supplier.id ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      updateMutation.mutate({
                        id: supplier.id,
                        body: {
                          name: editing.form.name.trim(),
                          contact: editing.form.contact.trim() || null,
                          notes: editing.form.notes.trim() || null,
                          ...gstPayload(editing.form),
                        },
                      });
                    }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
                      {supplier.code} — {supplier.name}
                    </p>
                    <div className="mt-3">
                      <SupplierFields
                        value={editing.form}
                        disabled={updateMutation.isPending}
                        gstinLocked={Boolean(supplier.gstin)}
                        onChange={(next) => setEditing({ ...editing, form: next })}
                      />
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Button type="submit" disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button tone="ghost" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                      {describeApiError(updateMutation.error) ? (
                        <span className="text-xs text-red-700">
                          {describeApiError(updateMutation.error)}
                        </span>
                      ) : null}
                    </div>
                  </form>
                ) : null
              }
            />
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
